import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

// Cache module-level pour éviter de re-fetcher les catégories sur chaque
// montage de composant. Les catégories bougent très peu.
let _cache = null;
let _inflight = null;

async function fetchCategories() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api
    .get("/beneficiary-categories")
    .then(({ data }) => {
      _cache = Array.isArray(data) ? data : [];
      return _cache;
    })
    .catch(() => {
      _cache = [];
      return _cache;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

/**
 * useBeneficiaryCategoryMap — récupère les catégories de bénéficiaires
 * (Clientèle, Partenaire, Fournisseur, Collaborateur…) et expose :
 *  - `categories` : liste brute [{ id, name, color, description }]
 *  - `idByName`   : map "clientèle" -> id (clé normalisée en lowercase)
 *  - `getIdByName(name)` : helper insensible à la casse/accents
 *
 * Le fetch est mis en cache pour toute l'app (les catégories bougent peu).
 */
export default function useBeneficiaryCategoryMap() {
  const [categories, setCategories] = useState(_cache || []);

  useEffect(() => {
    let mounted = true;
    fetchCategories().then((list) => {
      if (mounted) setCategories(list);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const idByName = useMemo(() => {
    const m = new Map();
    for (const c of categories || []) {
      if (c?.name) m.set(String(c.name).trim().toLowerCase(), c.id);
    }
    return m;
  }, [categories]);

  const getIdByName = (name) => {
    if (!name) return null;
    return idByName.get(String(name).trim().toLowerCase()) || null;
  };

  return { categories, idByName, getIdByName };
}
